import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, addDoc, setDoc, doc, getDocs, runTransaction, getDoc, query, orderBy, where, limit, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import SecureInput from '../common/SecureInput';
import { useDebounce } from '../../hooks/useDebounce';
import { logUserAction, logEntityChanges } from '../../utils/logger';
import { useNavigate } from 'react-router-dom';
import { sanitizeString, sanitizeNumber } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { formatDate, convertDateForSave } from '../../utils/dateHelpers';
import { generateLeadSourceId, generateOpportunityId } from '../../utils/documentIdUtils';
import { collectionPath } from '../../utils/firestoreCollections';
import { createNotification, notifyManagersAboutOpportunityExtension } from '../../utils/notifications';
import { useUserRole } from '../../hooks/useUserRole';
import { usePermissions } from '../../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { validateEntity } from '../../utils/schemaValidation';
import { handleFirestoreError, PerformanceMonitor } from '../../utils/errorHandling';
import { useGamification } from '../gamification/GamificationContext';
import { getOpenTasksForOpportunityStage } from '../../utils/associationUtils';
import { canAssignOpportunity, getOpportunityAssignmentUsers } from '../../utils/assignmentUsers';

/**
 * Stage progression order (for determining forward vs backward movement)
 */
const STAGE_ORDER = ['New', 'Qualified', 'Proposal Sent', 'Closed Won', 'Not Qualified', 'Closed Lost'];

/**
 * Opportunity Value Ranges
 * Used for dropdown selection instead of free-form numeric input
 */
export const VALUE_RANGES = [
  { value: '0-1000', label: '$0 - $1,000', min: 0, max: 1000, midpoint: 500 },
  { value: '1000-2500', label: '$1,000 - $2,500', min: 1000, max: 2500, midpoint: 1750 },
  { value: '2500-5000', label: '$2,500 - $5,000', min: 2500, max: 5000, midpoint: 3750 },
  { value: '5000-10000', label: '$5,000 - $10,000', min: 5000, max: 10000, midpoint: 7500 },
  { value: '10000-25000', label: '$10,000 - $25,000', min: 10000, max: 25000, midpoint: 17500 },
  { value: '25000-50000', label: '$25,000 - $50,000', min: 25000, max: 50000, midpoint: 37500 },
  { value: '50000-100000', label: '$50,000 - $100,000', min: 50000, max: 100000, midpoint: 75000 },
  { value: '100000+', label: '> $100,000', min: 100000, max: Infinity, midpoint: 150000 },
  // Legacy: kept for backward compat with existing data stored as '10000+'
  { value: '10000+', label: '> $10,000 (legacy)', min: 10000, max: 10000, midpoint: 17500, legacy: true },
];

/**
 * Get display label for a value range
 * @param {string|number} value - The value or value range key
 * @returns {string} Display label
 */
export function getValueRangeLabel(value) {
  if (!value && value !== 0) return '-';
  
  // If it's already a range key, find the label
  const range = VALUE_RANGES.find(r => r.value === value);
  if (range) return range.label;
  
  // Legacy: If it's a number, find which range it falls into
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (!isNaN(numValue)) {
    const matchingRange = VALUE_RANGES.find(r => numValue >= r.min && numValue < r.max);
    if (matchingRange) return matchingRange.label;
    // For values exactly at 10000+
    if (numValue >= 10000) return VALUE_RANGES[VALUE_RANGES.length - 1].label;
    return `$${numValue.toLocaleString()}`; // Fallback for legacy exact values
  }
  
  return String(value);
}

/**
 * Get numeric midpoint for a value range (for calculations/sorting)
 * @param {string|number} value - The value or value range key
 * @param {number} [actualValue] - Optional actual value (used for Closed Won opportunities)
 * @returns {number} Numeric value (actualValue if provided, otherwise midpoint)
 */
export function getValueRangeMidpoint(value, actualValue) {
  // If actualValue is provided and valid, use it (for Closed Won opportunities)
  if (actualValue !== undefined && actualValue !== null && actualValue !== '') {
    const numActual = typeof actualValue === 'number' ? actualValue : parseFloat(actualValue);
    if (!isNaN(numActual) && numActual > 0) return numActual;
  }
  
  if (!value && value !== 0) return 0;
  
  // If it's a range key, return the midpoint
  const range = VALUE_RANGES.find(r => r.value === value);
  if (range) return range.midpoint;
  
  // Legacy: If it's already a number, return it
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(numValue) ? 0 : numValue;
}

/**
 * Format a currency value for display
 * @param {number|string} value - The numeric value
 * @returns {string} Formatted currency string
 */
export function formatActualValue(value) {
  if (!value && value !== 0) return '-';
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '-';
  return `$${numValue.toLocaleString()}`;
}

const OppModal = ({ user, initial, customers = [], onClose, onSaved, users = [] }) => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(user);
  const userRole = useUserRole(user); // reliable admin detection
  const { recordActivity } = useGamification();

  // Helper to convert any date to yyyy-MM-dd for input[type="date"]
  function toInputDate(val) {
    if (!val) return '';
    // If already in yyyy-MM-dd, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    
    let d;
    // Handle Firestore Timestamp
    if (val.toDate && typeof val.toDate === 'function') {
      d = val.toDate();
    }
    // Handle Firestore Timestamp with seconds property
    else if (val.seconds) {
      d = new Date(val.seconds * 1000);
    }
    // Handle regular Date or string
    else {
      d = new Date(val);
    }
    
    if (!d || isNaN(d)) return '';
    
    // Use UTC methods to avoid timezone shifts
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // Use user's preferred format for display (e.g. in read-only fields or summary)
  // For input fields, always use yyyy-MM-dd

  const [customerId, setcustomerId] = useState(initial?.customerId || '');
  const [customerName, setcustomerName] = useState(initial?.customerName || ''); // Store customer name for denormalization
  const [stage, setStage] = useState(initial?.stage || 'New');
  const [value, setValue] = useState(initial?.value || '');
  const [actualValue, setActualValue] = useState(initial?.actualValue || ''); // Actual value captured on Closed Won
  const [startDate, setStartDate] = useState(
    toInputDate(initial?.startDate || initial?.date || new Date().toISOString().split('T')[0])
  );
  const [dueDate, setDueDate] = useState(
    toInputDate(
      initial?.dueDate || (
        !initial
          ? (() => {
              const today = new Date();
              const future = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
              return future.toISOString().split('T')[0];
            })()
          : ''
      )
    )
  );
  const [uid, setUid] = useState(initial?.uid || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [closedDate, setClosedDate] = useState(
    toInputDate(initial?.closedDate || '')
  );

  // Contact info for the opportunity
  const [contactName, setContactName] = useState(initial?.contactName || '');
  const [contactTitle, setContactTitle] = useState(initial?.contactTitle || '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail || '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone || '');

  // Lead Source dropdown state
  const [leadSource, setLeadSource] = useState(initial?.leadSource || '');
  const [leadSourceOptions, setLeadSourceOptions] = useState(['PGA Show', 'Local Show']);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newLeadSource, setNewLeadSource] = useState('');

  // Customer selector (cost‑effective: deferred load + client filtering)
  const [customerQuery, setcustomerQuery] = useState('');
  const [customerResults, setcustomerResults] = useState([]); // [{id, name}]
  const [customerLoading, setcustomerLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedcustomerQuery = useDebounce(customerQuery, 250);
  const [customerBase, setcustomerBase] = useState([]); // raw dataset loaded once
  const baseLoadedRef = useRef(false);

  // Inline customer creation state
  const [creatingNewCustomer, setcreatingNewCustomer] = useState(false);
  const [newcustomerName, setNewcustomerName] = useState('');
  const [newcustomerEmail, setNewcustomerEmail] = useState('');
  const [newcustomerPhone, setNewcustomerPhone] = useState('');
  const [newcustomerCity, setNewcustomerCity] = useState('');
  const [newcustomerState, setNewcustomerState] = useState('');
  const [newcustomerCountry, setNewcustomerCountry] = useState('');

  // Open tasks warning modal state
  const [showTaskWarning, setShowTaskWarning] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);
  const [openTasksData, setOpenTasksData] = useState({ openTasks: [], count: 0 });
  const [checkingTasks, setCheckingTasks] = useState(false);

  // Follow-up state (for Not Qualified / Closed Lost stages)
  const [stageChangeReason, setStageChangeReason] = useState(initial?.stageChangeReason || '');
  const [followUpFlag, setFollowUpFlag] = useState(!!initial?.followUpFlag);
  const [followUpDate, setFollowUpDate] = useState(initial?.followUpDate || '');
  const [followUpNotes, setFollowUpNotes] = useState(initial?.followUpNotes || '');

  // Search constraints (match CustomerPage behaviour)
  const MIN_CHARS = 2;             // require at least 2 chars before loading
  const MAX_CUSTOMER_FETCH = 5000;  // load all customers for accurate search results

  const navigate = useNavigate();

  // Fetch persisted Lead Sources from Firestore
  useEffect(() => {
    const fetchNames = async () => {
      const snap = await getDocs(collection(db, collectionPath('leadSource')));
      const names = snap.docs.map(doc => doc.data().name).filter(Boolean);
      setLeadSourceOptions(prev =>
        Array.from(new Set([...prev, ...names]))
      );
    };
    fetchNames();
  }, []);

  // Persist new Lead Source to Firestore
  const persistNewLeadSource = async (name) => {
    if (!name.trim()) return;
    // Only add if not already present
    if (!leadSourceOptions.includes(name.trim())) {
      const sourceId = generateLeadSourceId(name.trim());
      await setDoc(doc(db, collectionPath('leadSource'), sourceId), { name: name.trim() });
      setLeadSourceOptions(prev => [...prev, name.trim()]);
    }
  };

  /**
   * Handle stage change with open task check
   * When advancing stages, check if there are open tasks for the current stage
   * that should be completed first
   */
  const handleStageChange = async (newStage) => {
    console.log('[OppModal:handleStageChange] Called with:', { 
      newStage, 
      initialId: initial?.id, 
      initialStage: initial?.stage,
      hasInitial: !!initial 
    });
    
    // If editing and advancing stage, check for open tasks
    if (initial?.id && initial?.stage && newStage !== initial.stage) {
      const oldIndex = STAGE_ORDER.indexOf(initial.stage);
      const newIndex = STAGE_ORDER.indexOf(newStage);
      
      console.log('[OppModal:handleStageChange] Stage indices:', { 
        oldStage: initial.stage, 
        newStage, 
        oldIndex, 
        newIndex, 
        isForward: newIndex > oldIndex 
      });
      
      // Only check when moving FORWARD (index increases)
      // Terminal stages (Closed Won/Lost, Not Qualified) are at higher indexes
      if (newIndex > oldIndex && oldIndex >= 0) {
        console.log('[OppModal:handleStageChange] Moving forward - checking for open tasks...');
        setCheckingTasks(true);
        try {
          const result = await getOpenTasksForOpportunityStage(initial.id, initial.stage);
          console.log('[OppModal:handleStageChange] Open tasks result:', result);
          
          if (result.hasOpenTasks) {
            console.log('[OppModal:handleStageChange] Found open tasks! Showing warning modal.');
            // Show warning modal instead of immediately changing stage
            setOpenTasksData(result);
            setPendingStage(newStage);
            setShowTaskWarning(true);
            setCheckingTasks(false);
            return; // Don't change stage yet
          } else {
            console.log('[OppModal:handleStageChange] No open tasks found, allowing stage change.');
          }
        } catch (err) {
          console.error('[OppModal:handleStageChange] Error checking open tasks:', err);
          // Continue with stage change on error
        }
        setCheckingTasks(false);
      } else {
        console.log('[OppModal:handleStageChange] Not moving forward (backward or same stage), skipping task check.');
      }
    } else {
      console.log('[OppModal:handleStageChange] Not editing existing opportunity or same stage, skipping task check.');
    }
    
    // No open tasks or moving backward - proceed with change
    console.log('[OppModal:handleStageChange] Proceeding with stage change to:', newStage);
    setStage(newStage);
  };

  /**
   * Confirm stage change despite open tasks
   */
  const confirmStageChange = () => {
    if (pendingStage) {
      setStage(pendingStage);
    }
    setShowTaskWarning(false);
    setPendingStage(null);
    setOpenTasksData({ openTasks: [], count: 0 });
  };

  /**
   * Cancel stage change and keep current stage
   */
  const cancelStageChange = () => {
    setShowTaskWarning(false);
    setPendingStage(null);
    setOpenTasksData({ openTasks: [], count: 0 });
  };

  // When stage changes, set closedDate if needed and auto-update dueDate
  const stageChangeCount = useRef(0);
  useEffect(() => {
    if (stage === 'Closed Won' || stage === 'Closed Lost') {
      // Only set if not already set (for editing)
      if (!closedDate) {
        setClosedDate(new Date().toISOString().split('T')[0]);
      }
    } else {
      setClosedDate('');
    }

    // Auto-update due date to 7 days from tomorrow when stage changes on an existing opportunity
    // Skip the initial render (stageChangeCount === 0)
    stageChangeCount.current += 1;
    if (initial?.id && stageChangeCount.current > 1) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1); // Start counting from tomorrow (not today)
      const newDue = new Date(tomorrow);
      newDue.setDate(newDue.getDate() + 7); // 7 days after tomorrow
      const yyyy = newDue.getFullYear();
      const mm = String(newDue.getMonth() + 1).padStart(2, '0');
      const dd = String(newDue.getDate()).padStart(2, '0');
      setDueDate(`${yyyy}-${mm}-${dd}`);
    }
    // eslint-disable-next-line
  }, [stage]);

  // When editing, fetch the selected customer’s name once to display it
  useEffect(() => {
    (async () => {
      if (initial?.customerId) {
        // First, try using the denormalized customer name from the opportunity (fast path)
        if (initial.customerName) {
          setcustomerQuery(initial.customerName);
          setcustomerId(initial.customerId);
          setcustomerName(initial.customerName);
          return;
        }
        
        // Fallback: try to fetch customer (may fail if user doesn't have access)
        try {
          const snap = await getDoc(doc(db, collectionPath('customers'), initial.customerId));
          const data = snap.exists() ? snap.data() : null;
          if (data?.customerName) {
            setcustomerQuery(data.customerName); // show name in input
            setcustomerId(initial.customerId);
            setcustomerName(data.customerName); // Store customer name for denormalization
          }
        } catch (e) {
          // User may not have access to customer - use denormalized name if available
          if (initial.customerName) {
            setcustomerQuery(initial.customerName);
            setcustomerId(initial.customerId);
            setcustomerName(initial.customerName);
          } else {
            console.warn('Cannot fetch customer (permission denied), and no denormalized name available');
          }
        }
      }
    })();
    // eslint-disable-next-line
  }, [initial?.customerId, initial?.customerName]);

  // Load base customers only after min chars typed (role constrained), then client-filter like CustomerPage
  const ensureBaseLoaded = async () => {
    if (baseLoadedRef.current) return;
    setcustomerLoading(true);
    try {
      const cons = [];
      // For non-admins, we'll filter client-side by salesReps after fetching
      // to match CustomerPage behavior (no Firestore-level user filtering)
      const snap = await getDocs(
        query(
          collection(db, collectionPath('customers')),
          ...cons,
          orderBy('customerName', 'asc'),
          limit(MAX_CUSTOMER_FETCH)
        )
      );
      let base = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          customerName: data.customerName || '',
          customerEmail: data.customerEmail || '',
          customerPhone: data.customerPhone || '',
          customerCity: data.customerCity || '',
          customerState: data.customerState || '',
          customerStatus: data.customerStatus || '',
          customerAddress1: data.customerAddress1 || '',
          customerAddress2: data.customerAddress2 || '',
          customerZip: data.customerZip || '',
          customerCountry: data.customerCountry || '',
          customerWebsite: data.customerWebsite || '',
          uid: data.uid,
          salesReps: Array.isArray(data.salesReps) ? data.salesReps : [],
        };
      });
      
      // Filter by salesReps for non-admins (client-side, matching CustomerPage)
      if (!can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && user?.displayName) {
        base = base.filter(customer => {
          if (!Array.isArray(customer.salesReps)) return false;
          return customer.salesReps.some(rep => 
            rep.salesRepName && rep.salesRepName.toLowerCase() === (user.displayName || '').toLowerCase()
          );
        });
      }
      
      setcustomerBase(base);
      baseLoadedRef.current = true;
    } catch (e) {
      console.error('Customer picker load error:', e);
      setcustomerBase([]);
    } finally {
      setcustomerLoading(false);
    }
  };

  // Trigger load only when suggestions shown AND query length >= MIN_CHARS
  useEffect(() => {
    const q = (debouncedcustomerQuery || '').trim();
    if (showSuggestions && q.length >= MIN_CHARS) {
      ensureBaseLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuggestions, userRole, user?.uid, debouncedcustomerQuery]);

  // Debounced search value
  const searchLower = useMemo(
    () => (debouncedcustomerQuery || '').toLowerCase().trim(),
    [debouncedcustomerQuery]
  );

  // Client-side filter: search only by customer name
  const applyClientFilter = (list) => {
    if (!searchLower) return list;
    return list.filter(c => {
      // Search only customer name
      return (c.customerName || '').toLowerCase().includes(searchLower);
    });
  };

  // Recompute suggestions when search changes or base loads
  useEffect(() => {
    if (!baseLoadedRef.current || searchLower.length < MIN_CHARS) {
      setcustomerResults([]);
      return;
    }
    const filtered = applyClientFilter(customerBase)
      .map(c => ({ id: c.id, name: c.customerName }));
    setcustomerResults(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLower, customerBase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const enforcedUid = can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)
      ? uid
      : (initial?.uid || user?.uid || '');
    const existingAssignment = initial?.id && enforcedUid === initial.uid;
    const assignee = users.find(u => (u.uid || u.id) === enforcedUid);
    if (!existingAssignment && !canAssignOpportunity(assignee)) {
      setError('Select an active Sales Rep or Sales Manager for opportunity assignment.');
      return;
    }
    // Validate required fields
    if (!stage) {
      setError('Stage is required');
      return;
    }

    // Require reason when moving to Not Qualified / Closed Lost
    const isFollowUpStage = stage === 'Not Qualified' || stage === 'Closed Lost';
    if (isFollowUpStage && !stageChangeReason?.trim()) {
      setError('A reason is required when moving to ' + stage);
      return;
    }
    
    // Handle Inline customer creation
    let finalcustomerId = customerId;
    let finalcustomerName = customerName; // Use existing customerName by default
    if (creatingNewCustomer) {
      if (!newcustomerName?.trim()) {
        setError('Customer Name is required');
        return;
      }
      
      // Create the new customer first
      try {
        const salesRepUser = users.find(u => u.uid === uid || u.id === uid);
        const newCustomer = {
          customerName: sanitizeString(newcustomerName, 200),
          customerEmail: newcustomerEmail?.trim() || '',
          customerPhone: newcustomerPhone?.trim() || '',
          ...(newcustomerCity?.trim() && { customerCity: sanitizeString(newcustomerCity, 100) }),
          ...(newcustomerState?.trim() && { customerState: sanitizeString(newcustomerState, 50) }),
          ...(newcustomerCountry?.trim() && { customerCountry: sanitizeString(newcustomerCountry, 100) }),
          customerStatus: 'Lead',
          needsReview: true, // Needs review for eventual AM customer merge
          importedFrom: 'manual',
          createdAt: new Date(),
          createdBy: user?.uid || '',
          salesReps: salesRepUser ? [{
            salesRepName: salesRepUser.displayName || `${salesRepUser.firstName} ${salesRepUser.lastName}`,
            displayName: salesRepUser.displayName || `${salesRepUser.firstName} ${salesRepUser.lastName}`,
            splitPercent: 100
          }] : []
        };
        
        const customerRef = await addDoc(
          collection(db, collectionPath('customers')),
          newCustomer
        );
        
        // Log customer creation
        await logEntityChanges({
          oldObj: null,
          newObj: { ...newCustomer, id: customerRef.id },
          fields: ['customerName', 'customerEmail', 'customerPhone', 'customerStatus'],
          entityType: 'Customer',
          entityId: customerRef.id,
          user: user,
          action: 'Created'
        });
        
        finalcustomerId = customerRef.id;
        finalcustomerName = newcustomerName.trim(); // Use the new customer name for denormalization
        setcustomerName(newcustomerName); // Update state for UI consistency
      } catch (err) {
        setError(`Failed to create customer: ${err.message}.`);
        setSaving(false);
        return;
      }
    } else if (!finalcustomerId) {
      setError('Customer is required. Choose an existing one from the search box or click Create as new customer.');
      return;
    }
    
    if (!leadSource) {
      setError('Lead Source is required.');
      return;
    }
    if (!startDate) {
      setError('Start Date is required.');
      return;
    }
    if (!dueDate) {
      setError('Due Date is required.');
      return;
    }
    if (!uid) {
      setError('Sales Rep is required.');
      return;
    }
    // Potential Value is required when stage is NOT "New"
    // This allows importing/creating opportunities without a value initially,
    // but requires it before advancing to other stages
    if (stage !== 'New' && !value) {
      setError('Potential Value is required when stage is not "New". Please select a value range before advancing the stage.');
      return;
    }
    // Require actual value when stage is Closed Won
    if (stage === 'Closed Won' && (!actualValue || parseFloat(actualValue) <= 0)) {
      setError('Actual Value is required for Closed Won opportunities.');
      return;
    }
    setSaving(true);

    let opportunityNumber = '';
    let oppDocId = ''; // Document ID for the opportunity
    let data = null; // Declare outside try block for catch block access
    let perfId = null; // Declare outside try block for catch block access
    
    try {
      if (!initial) {
        const counterRef = doc(db, collectionPath('counters', 'opportunityNumber'));
        await runTransaction(db, async (transaction) => {
          const counterDoc = await transaction.get(counterRef);
          if (!counterDoc.exists()) {
            transaction.set(counterRef, { next: 2 });
            opportunityNumber = 1;
          } else {
            const next = counterDoc.data().next || 1;
            opportunityNumber = next;
            transaction.update(counterRef, { next: next + 1 });
          }
        });
        oppDocId = generateOpportunityId(opportunityNumber);
      }

      data = {
        leadSource: sanitizeString(leadSource, 200),
        customerId: finalcustomerId,
        customerName: sanitizeString(finalcustomerName, 200), // Denormalize customer name to avoid lookups
        stage,
        value: value || '', // Store the range key string (e.g., '10000-25000')
        ...(stage === 'Closed Won' && actualValue && { actualValue: parseFloat(actualValue) }), // Actual value for Closed Won
        startDate: convertDateForSave(startDate), // Convert to Timestamp
        dueDate: convertDateForSave(dueDate), // Convert to Timestamp
        uid: enforcedUid, // <-- use uid here
        opportunityNumber: initial?.opportunityNumber || opportunityNumber || '',
        opportunityNumberLabel: initial?.opportunityNumberLabel || (oppDocId || initial?.id || ''), // Human-readable ID (OPP-#)
        ...(closedDate && { closedDate: convertDateForSave(closedDate) }), // Convert to Timestamp
        ...(contactName?.trim() && { contactName: sanitizeString(contactName, 200) }),
        ...(contactTitle?.trim() && { contactTitle: sanitizeString(contactTitle, 100) }),
        ...(contactEmail?.trim() && { contactEmail: contactEmail.trim().toLowerCase() }),
        ...(contactPhone?.trim() && { contactPhone: sanitizeString(contactPhone, 20) }),
        updatedAt: serverTimestamp(), // Auto-track when record was modified
      };

      // Follow-up fields for Not Qualified / Closed Lost
      const isFollowUpStage = stage === 'Not Qualified' || stage === 'Closed Lost';
      if (isFollowUpStage) {
        data.stageChangeReason = sanitizeString(stageChangeReason, 1000);
        data.followUpFlag = followUpFlag;
        data.followUpDate = followUpFlag ? sanitizeString(followUpDate, 20) : '';
        data.followUpNotes = followUpFlag ? sanitizeString(followUpNotes, 1000) : '';
        if (followUpFlag) {
          // Set dueDate to the follow-up date for re-engagement tracking
          if (followUpDate) {
            data.dueDate = convertDateForSave(followUpDate);
          }
          // Always ensure followUpSetBy fields are populated (backfill if missing)
          if (!initial?.followUpFlag) {
            data.followUpSetByUid = user.uid;
            data.followUpSetByName = user.displayName || user.email;
            data.followUpSetAt = serverTimestamp();
            data.followUpCompleted = false;
            data.followUpCompletedAt = null;
            data.followUpCompletedByUid = null;
          } else if (!initial?.followUpSetByName) {
            // Backfill: existing flag is on but name was never recorded
            data.followUpSetByUid = initial.followUpSetByUid || user.uid;
            data.followUpSetByName = user.displayName || user.email;
            if (!initial.followUpSetAt) {
              data.followUpSetAt = serverTimestamp();
            }
          }
        }
      }
      
      // Determine if this is a "New Account" opportunity (for reporting purposes)
      // New Account = inline-created customer OR customer status='Lead' OR customer created within 30 days
      let isNewAccount = false;
      if (creatingNewCustomer) {
        // Inline-created customers are always new accounts
        isNewAccount = true;
      } else if (finalcustomerId && !initial) {
        // For new opportunities, check the selected customer's status and age
        const selectedCustomer = customerBase.find(c => c.id === finalcustomerId);
        if (selectedCustomer) {
          // Check if customer status is 'Lead'
          if (selectedCustomer.customerStatus === 'Lead') {
            isNewAccount = true;
          } else {
            // Check if customer was created within the last 30 days
            // Need to fetch customer doc to get createdAt
            try {
              const customerDoc = await getDoc(doc(db, collectionPath('customers'), finalcustomerId));
              if (customerDoc.exists()) {
                const customerData = customerDoc.data();
                if (customerData.createdAt) {
                  const createdDate = customerData.createdAt.toDate ? customerData.createdAt.toDate() : new Date(customerData.createdAt);
                  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                  if (createdDate >= thirtyDaysAgo) {
                    isNewAccount = true;
                  }
                }
              }
            } catch (err) {
              console.warn('Could not check customer creation date for new account flag:', err);
              // Default to existing account if we can't determine
            }
          }
        }
      }
      
      // Add isNewAccount flag to data (only set for new opportunities, preserve existing value for edits)
      if (!initial) {
        data.isNewAccount = isNewAccount;
      } else if (initial.isNewAccount !== undefined) {
        // Preserve the original value when editing
        data.isNewAccount = initial.isNewAccount;
      }
      
      // Add createdAt only for new opportunities
      if (!initial) {
        data.createdAt = serverTimestamp();
      }
      
      // Validate schema before saving
      const validation = validateEntity(data, 'opportunity');
      if (!validation.valid) {
        setError(validation.errors[0] || 'Invalid opportunity data');
        setSaving(false);
        return;
      }
      
      perfId = PerformanceMonitor.startOperation(
        initial ? 'update-opportunity' : 'create-opportunity'
      );

      let oppId = '';
      
      if (initial) {
        // Save the updated opportunity
        await setDoc(doc(db, collectionPath('opportunities', initial.id)), {
          ...data,
          ...(initial?.qualification && { qualification: initial.qualification })
        });

        // Log the changes using logEntityChanges (only one log entry!)
        await logEntityChanges({
          oldObj: initial,
          newObj: { ...initial, ...data },
          fields: [
            'customerId',
            'customerName',
            'leadSource',
            'stage',
            'value',
            'uid',
            'startDate',
            'dueDate',                        
            'closedDate',
            'contactName',
            'contactEmail',
            'contactPhone',
            'contactTitle',
            'isNewAccount'
          ],
          entityType: 'Opportunity',
          entityId: initial.id,
          user,
          action: 'Updated',
          extraDetails: `Opportunity (${initial.opportunityNumberLabel}) was updated by ${(() => {
            // Show sales rep name in extraDetails
            const rep = users.find(u => u.id === (uid || user.uid)) || user;
            return rep.firstName && rep.lastName
                ? `${rep.firstName} ${rep.lastName}`
                : rep.displayName || rep.email || rep.id || '-';
          })()}`,
          fieldMap: {
            customerId: (id) => {
              const customer = customers.find(c => c.id === id);
              return customer ? customer.name : id;
            },
            uid: (id) => {
              const rep = users.find(u => u.id === id);
              return rep
                  ? ((rep.firstName && rep.lastName)
                      ? `${rep.firstName} ${rep.lastName}`
                      : rep.displayName || rep.email || rep.id)
                  : id;
            }
          }
        });

        oppId = initial.id;
      } else {
        // Create the opportunity with human-readable document ID (OPP-#)
        await setDoc(doc(db, collectionPath('opportunities'), oppDocId), data);
        oppId = oppDocId;
      }

      // Auto-create contact in standalone contacts collection if contact info provided
      if (contactName?.trim() && contactEmail?.trim() && finalcustomerId) {
        try {
          const normalizedEmail = contactEmail.trim().toLowerCase();

          // Check if contact already exists in standalone contacts collection (by email + customer)
          const existingContactQ = query(
            collection(db, collectionPath('contacts')),
            where('customerId', '==', finalcustomerId),
            where('email', '==', normalizedEmail)
          );
          const existingContactSnap = await getDocs(existingContactQ);

          if (existingContactSnap.empty) {
            // Split name into firstName/lastName
            const nameParts = sanitizeString(contactName, 200).split(' ').filter(Boolean);
            let firstName = '';
            let lastName = '';

            if (nameParts.length === 1) {
              firstName = nameParts[0];
            } else if (nameParts.length >= 2) {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            }

            // Look up customer name for denormalization
            let contactCustomerName = finalcustomerName || '';
            if (!contactCustomerName) {
              try {
                const custSnap = await getDoc(doc(db, collectionPath('customers'), finalcustomerId));
                if (custSnap.exists()) contactCustomerName = custSnap.data().customerName || '';
              } catch (_) { /* non-critical */ }
            }

            const contactId = Date.now().toString();
            const contactPayload = {
              contactId: contactId,
              contactCustomerId: finalcustomerId,
              customerId: finalcustomerId,
              customerName: sanitizeString(contactCustomerName, 200),
              firstName: firstName,
              lastName: lastName,
              email: normalizedEmail,
              phone: contactPhone ? sanitizeString(contactPhone, 20) : '',
              contactPosition: contactTitle ? sanitizeString(contactTitle, 100) : '',
              contactFocus: [],
              isPrimary: false,
              updatedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
              sourceCreation: 'opportunity',
              createdBy: user?.uid || ''
            };

            await setDoc(doc(db, collectionPath('contacts'), contactId), contactPayload);

            // Log contact creation
            await logEntityChanges({
              oldObj: null,
              newObj: contactPayload,
              fields: ['firstName', 'lastName', 'email', 'phone', 'contactPosition'],
              entityType: 'Contact',
              entityId: contactId,
              user,
              action: 'Created',
              extraDetails: `Contact "${contactName}" (${normalizedEmail}) auto-created from opportunity`
            });
          }
        } catch (contactErr) {
          console.error('Failed to auto-create contact:', contactErr);
          // Don't fail the whole operation if contact creation fails
        }
      }

      // Return success message to parent
      const successMessage = initial 
        ? `Opportunity ${initial.opportunityNumberLabel || ''} updated successfully`
        : `Opportunity ${opportunityNumber || ''} created successfully`;
      PerformanceMonitor.endOperation(perfId, true);
      
      // Gamification: Award points for opportunity activities (with anti-abuse metadata)
      try {
        if (!initial) {
          // New opportunity created - pass entity data for validation
          await recordActivity('OPPORTUNITY_CREATED', { 
            entityId: oppDocId,
            source: 'manual',
            entityData: { customerName, value }
          });
        } else if (stage !== initial.stage) {
          // Stage changed
          if (stage === 'Closed Won') {
            // Deal won! Award points based on value
            await recordActivity('OPPORTUNITY_WON', { 
              dealValue: Number(value) || 0,
              entityId: initial.id,
              source: 'manual'
            });
          } else if (stage !== 'Closed Lost' && stage !== 'Not Qualified') {
            // Stage advanced (but not to terminal stages) - validate forward movement
            await recordActivity('OPPORTUNITY_STAGE_ADVANCED', {
              entityId: initial.id,
              source: 'manual',
              oldStage: initial.stage,
              newStage: stage
            });
          }
        } else {
          // General update - check for meaningful changes
          await recordActivity('OPPORTUNITY_UPDATED', {
            entityId: initial.id,
            source: 'manual',
            oldData: initial,
            entityData: data,
            fieldsToCheck: ['customerName', 'name', 'value', 'notes', 'probability', 'expectedCloseDate']
          });
        }
      } catch (gamErr) {
        console.warn('Gamification tracking failed:', gamErr);
        // Don't fail the operation if gamification fails
      }
      
      onSaved(successMessage);
      setSaving(false);
      } catch (err) {
        if (perfId) {
          PerformanceMonitor.endOperation(perfId, false);
        }
        console.error('Failed to save opportunity:', err);
        const friendlyMsg = handleFirestoreError(
          err,
          initial ? 'update-opportunity' : 'create-opportunity',
          data ? { customerName: data.customerName } : {}
        );
        setError(friendlyMsg);
        setSaving(false);
      }
  };


  // allow stage edits for admins OR the sales rep assigned to this opportunity (when editing)
  const canEditStage = can(FEATURE_PERMISSIONS.OPP_EDIT) || (initial && (initial.uid === user?.uid || initial.uid === user?.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <form 
        onSubmit={handleSubmit} 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md my-4 flex flex-col max-h-[calc(100vh-2rem)]"
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{initial ? 'Edit' : 'Add'} Opportunity</h2>
          <button 
            type="button" 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {error && <p className="text-red-500 dark:text-red-400">{error}</p>}

        {/* Opportunity Number (read-only, only on edit) */}
        {initial?.opportunityNumberLabel && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opportunity #</label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded cursor-not-allowed"
              value={initial.opportunityNumberLabel}
              readOnly
              disabled
            />
          </div>
        )}

        {/* 1. Stage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stage <span className="text-red-600">*</span></label>
          {canEditStage ? (
            <select
              className={`w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded ${checkingTasks ? 'opacity-50' : ''}`}
              value={stage}
              onChange={e => handleStageChange(e.target.value)}
              disabled={checkingTasks}
              required
            >
              <option value="">Select Stage</option>
              <option value="New">New</option>
              <option value="Qualified">Qualified</option>              
              <option value="Not Qualified">Not Qualified</option>
              <option value="Proposal Sent">Proposal Sent</option>
              <option value="Closed Won">Closed Won</option>
              <option value="Closed Lost">Closed Lost</option>
            </select>
          ) : (
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded cursor-not-allowed"
              value={stage}
              readOnly
              disabled
            />
          )}
        </div>

        {/* Closed Date (only show if stage is Closed Won or Closed Lost) */}
        {(stage === 'Closed Won' || stage === 'Closed Lost') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Closed Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded cursor-not-allowed"
              value={closedDate}
              readOnly
              disabled
            />
          </div>
        )}

        {/* Follow-Up fields (for Not Qualified / Closed Lost) */}
        {(stage === 'Not Qualified' || stage === 'Closed Lost') && (
          <div className="col-span-2 border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-r-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Stage Change Details</span>
            </div>

            {/* Required reason field */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Reason for Stage Change <span className="text-red-600">*</span>
              </label>
              <textarea
                value={stageChangeReason}
                onChange={(e) => setStageChangeReason(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Why is this opportunity being moved to this stage?"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              />
            </div>

            {/* Optional re-engagement section */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Optionally flag for future re-engagement:</p>
            <label className="flex items-center gap-3 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={followUpFlag}
                onChange={(e) => setFollowUpFlag(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Flag for future re-engagement</span>
            </label>
            {followUpFlag && (
              <div className="space-y-3 pl-7">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Follow-Up Date</label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-48 px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
                  <textarea
                    value={followUpNotes}
                    onChange={(e) => setFollowUpNotes(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="Reason for re-engagement, key details…"
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2. Customer (typeahead) */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer <span className="text-red-600">*</span></label>
          
          {!creatingNewCustomer ? (
            <>
              {/* Show read-only customer name if editing and customer is already set (user may not have access) */}
              {initial && customerId && customerName ? (
                <>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded cursor-not-allowed"
                    value={customerName}
                    readOnly
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Customer cannot be changed when editing. Create a new opportunity to use a different customer.
                  </p>
                </>
              ) : (
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                  placeholder={`Type at least ${MIN_CHARS} letters to search customers...`}
                  value={customerQuery}
                  onChange={e => {
                    setcustomerQuery(e.target.value);
                    setShowSuggestions(true);
                    // Clear selection if user changes text
                    if (customerId && e.target.value.trim().toLowerCase() !== (customerQuery || '').toLowerCase()) {
                      setcustomerId('');
                    }
                  }}
                  onFocus={() => { setShowSuggestions(true); /* base will load after {MIN_CHARS}+ chars */ }}
                  required={!creatingNewCustomer}
                />
              )}
              {/* Only show suggestions dropdown if creating new opportunity or customer not locked */}
              {showSuggestions && !(initial && customerId) && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow max-h-64 overflow-auto">
                  {((debouncedcustomerQuery || '').trim().length < MIN_CHARS) && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Type at least {MIN_CHARS} characters to search
                    </div>
                  )}
                  {customerLoading && (
                    <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
                  )}
                  {!customerLoading &&
                    (debouncedcustomerQuery || '').trim().length >= MIN_CHARS &&
                    customerResults.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No customers found.
                        {/* Show Create New Customer button inside dropdown when no results */}
                        <button
                          type="button"
                          onClick={() => {
                            setcreatingNewCustomer(true);
                            setNewcustomerName(customerQuery.trim()); // Pre-fill with search query
                            setShowSuggestions(false);
                          }}
                          className="block mt-2 text-yellow-600 hover:text-yellow-800 font-medium"
                        >
                          + Create "{customerQuery.trim()}" as new customer
                        </button>
                      </div>
                  )}
                  {!customerLoading && customerResults.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => {
                        setcustomerId(c.id);
                        setcustomerName(c.name); // Store customer name for denormalization
                        setcustomerQuery(c.name);
                        setShowSuggestions(false);
                      }}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
              {customerId && !initial && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected ID: {customerId} <button type="button" className="ml-2 text-indigo-600 hover:text-indigo-800" onClick={() => { setcustomerId(''); setcustomerQuery(''); setShowSuggestions(true); }}>Change</button>
                </p>
              )}
              
              {/* Button to toggle Inline customer creation (only when creating new opportunity and dropdown not showing) */}
              {!customerId && !initial && !showSuggestions && (
                <button
                  type="button"
                  onClick={() => {
                    setcreatingNewCustomer(true);
                    setShowSuggestions(false);
                  }}
                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  + Create New Customer
                </button>
              )}
            </>
          ) : (
            /* Inline customer creation form */
            <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Create New Customer</h4>
                <button
                  type="button"
                  onClick={() => {
                    setcreatingNewCustomer(false);
                    setNewcustomerName('');
                    setNewcustomerEmail('');
                    setNewcustomerPhone('');
                    setNewcustomerCity('');
                    setNewcustomerState('');
                    setNewcustomerCountry('');
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Cancel
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                    placeholder="Enter Company Name"
                    value={newcustomerName}
                    onChange={(e) => setNewcustomerName(e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                    placeholder="Enter contact person's name"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    maxLength={200}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional: Your contact at this company
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                    placeholder="contact@company.com"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      // Sync with customer email for customer record
                      setNewcustomerEmail(e.target.value);
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional: Will be saved to both customer and opportunity
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                    placeholder="555-1234"
                    value={newcustomerPhone}
                    onChange={(e) => setNewcustomerPhone(e.target.value)}
                  />
                </div>
                
                {/* Customer Location Fields */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                      placeholder="City"
                      value={newcustomerCity}
                      onChange={(e) => setNewcustomerCity(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                      placeholder="State"
                      value={newcustomerState}
                      onChange={(e) => setNewcustomerState(e.target.value)}
                      maxLength={50}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded text-sm"
                      placeholder="Country"
                      value={newcustomerCountry}
                      onChange={(e) => setNewcustomerCountry(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Company will be created as a "Lead" and assigned to the selected sales rep.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Contact Information - Hidden when dropdown is open or creating new customer to avoid overlap */}
        {!showSuggestions && !creatingNewCustomer && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                placeholder="Enter contact name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                maxLength={200}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional: Person you're working with at this customer
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Title</label>
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                placeholder="e.g., Buyer, Golf Pro, Manager"
                value={contactTitle}
                onChange={(e) => setContactTitle(e.target.value)}
                maxLength={100}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional: Contact's job title or role
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
              <input
                type="email"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                placeholder="contact@customer.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional: Will auto-populate when sending emails
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Phone</label>
              <input
                type="tel"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                placeholder="(555) 123-4567"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                maxLength={20}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional: Contact's phone number
              </p>
            </div>
          </>
        )}

        {/* 3. Lead Source */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lead Source <span className="text-red-600">*</span></label>
          <div className="flex">
            <select
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
              value={leadSourceOptions.includes(leadSource) ? leadSource : ''}
              onChange={e => {
                if (e.target.value === '__add_new__') {
                  setShowAddNew(true);
                } else {
                  setLeadSource(e.target.value);
                }
              }}
              required={!leadSource}
            >
              <option value="">Select Lead Source</option>
              {leadSourceOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
              <option value="__add_new__">Add New...</option>
            </select>
          </div>
          {/* Show text input if adding new */}
          {showAddNew && (
            <div className="mt-2 flex">
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                placeholder="Enter new Lead Source"
                value={newLeadSource}
                onChange={e => setNewLeadSource(e.target.value)}
              />
              <button
                type="button"
                className="ml-2 px-3 py-2 bg-indigo-600 text-white rounded"
                onClick={async () => {
                  const trimmed = newLeadSource.trim();
                  if (trimmed && !leadSourceOptions.includes(trimmed)) {
                    await persistNewLeadSource(trimmed);
                    setLeadSource(trimmed);
                  }
                  setShowAddNew(false);
                  setNewLeadSource('');
                }}
              >
                Add
              </button>
              <button
                type="button"
                className="ml-2 px-3 py-2 bg-gray-300 text-gray-800 rounded"
                onClick={() => {
                  setShowAddNew(false);
                  setNewLeadSource('');
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {/* If a custom value is set (not in the list), show it as a text field */}
          {!leadSourceOptions.includes(leadSource) && !showAddNew && leadSource && (
            <div className="mt-2">
              <input
                type="text"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
                value={leadSource}
                onChange={e => setLeadSource(e.target.value)}
                placeholder="Lead Source"
                required
              />
            </div>
          )}
        </div>

        {/* 4. Value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Potential Value {stage !== 'New' && <span className="text-red-600">*</span>}
          </label>
          <select
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
            value={value}
            onChange={e => setValue(e.target.value)}
          >
            <option value="">Select value range...</option>
            {VALUE_RANGES.filter(r => !r.legacy).map(range => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
          {stage === 'New' && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional for "New" stage - required before advancing to other stages
            </p>
          )}
        </div>

        {/* 4b. Actual Value - only shown for Closed Won */}
        {stage === 'Closed Won' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Actual Value <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="number"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 pl-7 rounded"
                value={actualValue}
                onChange={e => setActualValue(e.target.value)}
                placeholder="Enter actual deal value"
                min="0"
                step="0.01"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter the actual value of this closed deal for accurate revenue reporting.
            </p>
          </div>
        )}

        {/* 5. Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date <span className="text-red-600">*</span></label>
          <input
            type="date"
            className={`w-full border border-gray-300 dark:border-gray-600 p-2 rounded ${
              initial 
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed' 
                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
            }`}
            value={startDate}
            onChange={e => !initial && setStartDate(e.target.value)}
            disabled={!!initial}
            required
            title={initial ? 'Start date cannot be changed after creation' : ''}
          />
          {initial && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Start date cannot be changed after creation.
            </p>
          )}
        </div>

        {/* 6. Due Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date <span className="text-red-600">*</span></label>
          <div className="flex items-center space-x-2">
            <input
              type="date"
              className={`flex-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded ${
                initial && !can(FEATURE_PERMISSIONS.OPP_EDIT) ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
              }`}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              disabled={initial && !can(FEATURE_PERMISSIONS.OPP_EDIT)}
              required
              title={
                initial && !can(FEATURE_PERMISSIONS.OPP_EDIT)
                  ? (initial.extensionCount >= 1 
                      ? 'Due date cannot be changed. You have already used your one-time 30-day extension. Please contact your manager if you need further assistance.'
                      : 'Due date cannot be changed manually. Use the "Extend +30d" button below for a one-time extension.')
                  : ''
              }
            />
            {/* Show extend button only when editing, user can edit, and hasn't already extended */}
            {initial && (can(FEATURE_PERMISSIONS.OPP_EDIT) || (initial && (initial.uid === user?.uid || initial.uid === user?.id))) && (
              <>
                {can(FEATURE_PERMISSIONS.OPP_EDIT) ? (
                  // Admins can always extend (no limit)
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = window.confirm('Extending the due date will notify your sales manager. Continue?');
                      if (!ok) return;
                      setSaving(true);
                      try {
                        // compute new date (30 days)
                        const current = toInputDate(initial.dueDate || dueDate) || dueDate || new Date().toISOString().split('T')[0];
                        const cur = new Date(current);
                        const newD = new Date(cur.getTime() + 30 * 24 * 60 * 60 * 1000);
                        const newDateStr = newD.toISOString().split('T')[0];
                        const dueTimestamp = convertDateForSave(newDateStr);
                        const newExtensionCount = (initial.extensionCount || 0) + 1;

                        // Format dates as MM/DD/YYYY for email
                        const formatDateForEmail = (dateStr) => {
                          const d = new Date(dateStr);
                          return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
                        };
                        const oldDueDateFormatted = formatDateForEmail(current);
                        const newDueDateFormatted = formatDateForEmail(newDateStr);

                        // Update opportunity dueDate and increment extensionCount
                        await setDoc(doc(db, collectionPath('opportunities'), initial.id), { 
                          dueDate: dueTimestamp, 
                          extensionCount: newExtensionCount,
                          updatedAt: serverTimestamp() 
                        }, { merge: true });

                        // Log change
                        await logEntityChanges({
                          oldObj: initial,
                          newObj: { ...initial, dueDate: dueTimestamp, extensionCount: newExtensionCount },
                          fields: ['dueDate', 'extensionCount'],
                          entityType: 'Opportunity',
                          entityId: initial.id,
                          user,
                          action: 'Updated',
                          extraDetails: `Due date extended from ${oldDueDateFormatted} to ${newDueDateFormatted} (Extension #${newExtensionCount})`
                        });

                        // Notify managers (email + in-app notification)
                        await notifyManagersAboutOpportunityExtension({ 
                          opportunity: { id: initial.id, opportunityNumberLabel: initial.opportunityNumberLabel, customerName: initial.customerName }, 
                          byUser: user,
                          oldDueDate: oldDueDateFormatted,
                          newDueDate: newDueDateFormatted
                        });

                        // Update local state and inform parent
                        setDueDate(newDateStr);
                        onSaved(`Due date extended successfully to ${newDueDateFormatted}`);
                      } catch (err) {
                        console.error('Extend due date failed', err);
                        setError(err.message || 'Failed to extend due date');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm transition-colors"
                    title="Admin: Extend due date by 30 days (unlimited extensions)"
                  >
                    Extend +30d
                  </button>
                ) : (
                  // Non-admins: check if they've already used their one extension
                  (initial.extensionCount || 0) < 1 ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = window.confirm('Extending the due date will notify your sales manager. This is a one-time extension. Continue?');
                        if (!ok) return;
                        setSaving(true);
                        try {
                          // compute new date (30 days)
                          const current = toInputDate(initial.dueDate || dueDate) || dueDate || new Date().toISOString().split('T')[0];
                          const cur = new Date(current);
                          const newD = new Date(cur.getTime() + 30 * 24 * 60 * 60 * 1000);
                          const newDateStr = newD.toISOString().split('T')[0];
                          const dueTimestamp = convertDateForSave(newDateStr);

                          // Format dates as MM/DD/YYYY for email
                          const formatDateForEmail = (dateStr) => {
                            const d = new Date(dateStr);
                            return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
                          };
                          const oldDueDateFormatted = formatDateForEmail(current);
                          const newDueDateFormatted = formatDateForEmail(newDateStr);

                          // Update opportunity dueDate and set extensionCount to 1
                          await setDoc(doc(db, collectionPath('opportunities'), initial.id), { 
                            dueDate: dueTimestamp, 
                            extensionCount: 1,
                            updatedAt: serverTimestamp() 
                          }, { merge: true });

                          // Log change
                          await logEntityChanges({
                            oldObj: initial,
                            newObj: { ...initial, dueDate: dueTimestamp, extensionCount: 1 },
                            fields: ['dueDate', 'extensionCount'],
                            entityType: 'Opportunity',
                            entityId: initial.id,
                            user,
                            action: 'Updated',
                            extraDetails: `Due date extended from ${oldDueDateFormatted} to ${newDueDateFormatted} (one-time extension used)`
                          });

                          // Notify managers (email + in-app notification)
                          await notifyManagersAboutOpportunityExtension({ 
                            opportunity: { id: initial.id, opportunityNumberLabel: initial.opportunityNumberLabel, customerName: initial.customerName }, 
                            byUser: user,
                            oldDueDate: oldDueDateFormatted,
                            newDueDate: newDueDateFormatted
                          });

                          // Update local state and inform parent
                          setDueDate(newDateStr);
                          onSaved(`Due date extended successfully to ${newDueDateFormatted}`);
                        } catch (err) {
                          console.error('Extend due date failed', err);
                          setError(err.message || 'Failed to extend due date');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm transition-colors"
                      title="Extend due date by 30 days (one-time only)"
                    >
                      Extend +30d
                    </button>
                  ) : (
                    <div className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-sm">
                      Extension Used
                    </div>
                  )
                )}
              </>
            )}
          </div>
          {/* Help text for non-admins */}
          {initial && !can(FEATURE_PERMISSIONS.OPP_EDIT) && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {(initial.extensionCount || 0) >= 1 ? (
                <>
                  ⚠️ You have used your one-time 30-day extension. Contact your manager for additional time.
                </>
              ) : (
                <>
                  💡 Use the "Extend +30d" button for a one-time extension. Managers will be notified.
                </>
              )}
            </p>
          )}
        </div>

        {/* 7. Sales Rep */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sales Rep <span className="text-red-600">*</span></label>
          <select
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded"
            value={uid}
            onChange={e => setUid(e.target.value)}
            aria-label="Sales Rep"
            required
          >
            <option value="">Unassigned</option>
            {getOpportunityAssignmentUsers(
                can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)
                  ? users
                  : users.filter(u => u.uid === user?.uid || (initial?.id && u.uid === initial.uid)),
                initial?.id ? initial.uid : ''
              )
                  .sort((a, b) => {
                    const aName = a.displayName ||
                      ((a.firstName || '') + ' ' + (a.lastName || '')).trim() ||
                      a.email || '';
                    const bName = b.displayName ||
                      ((b.firstName || '') + ' ' + (b.lastName || '')).trim() ||
                      b.email || '';
                    return aName.localeCompare(bName);
                  })
                  .map(u => {
                    const label = u.displayName ||
                      ((u.firstName || '') + ' ' + (u.lastName || '')).trim() ||
                      u.email ||
                      u.uid;
                    return (
                      <option key={u.uid} value={u.uid} disabled={!canAssignOpportunity(u)}>
                        {label}{u.role === 'disabled' ? ' (Deactivated)' : !canAssignOpportunity(u) ? ' (Not eligible for new assignments)' : ''}
                      </option>
                    );
                  })
            }
          </select>
        </div>

        </div>
        
        {/* Footer - Fixed */}
        <div className="flex justify-end space-x-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded hover:bg-indigo-700 dark:hover:bg-indigo-400 transition-colors disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </form>

      {/* Open Tasks Warning Modal */}
      {showTaskWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-500 px-4 py-3">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Open Tasks Warning
              </h3>
            </div>
            <div className="p-4">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                This opportunity has <strong className="text-amber-600">{openTasksData.count} open task{openTasksData.count !== 1 ? 's' : ''}</strong> for the <strong>"{initial?.stage}"</strong> stage that should be completed before advancing to <strong>"{pendingStage}"</strong>.
              </p>
              
              {openTasksData.openTasks.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Open Tasks:</p>
                  <ul className="space-y-1">
                    {openTasksData.openTasks.map(task => (
                      <li key={task.id} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                        <span className="text-amber-500 mr-2">•</span>
                        <span>
                          <strong>{task.taskLabel}</strong>: {task.title || task.subject || 'Untitled task'}
                          {task.dueDate && (
                            <span className="text-xs text-gray-500 ml-2">
                              (Due: {formatDate(task.dueDate)})
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to advance the stage anyway?
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-4 py-3 bg-gray-50 dark:bg-gray-700">
              <button
                type="button"
                onClick={cancelStageChange}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Stay on "{initial?.stage}"
              </button>
              <button
                type="button"
                onClick={confirmStageChange}
                className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors"
              >
                Advance Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OppModal;
